"use client";

import { useEffect } from "react";
import { Button } from "@astryxdesign/core/Button";
import { Icon } from "@astryxdesign/core/Icon";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import { researchIcons } from "@/lib/icons/research-icons";

type Variant = "offer" | "success" | "info";

type Props = {
  variant: Variant;
  title: string;
  benefitLabel: string;
  description: string;
  ctaLabel: string;
  busy?: boolean;
  error?: string | null;
  onPrimary: () => void;
  onClose: () => void;
};

/**
 * Универсальный стеклянный попап, когда нет картинок баннеров.
 */
export function PromoGlassModal({
  variant,
  title,
  benefitLabel,
  description,
  ctaLabel,
  busy = false,
  error,
  onPrimary,
  onClose,
}: Props) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const icon =
    variant === "success"
      ? researchIcons.checkCircle
      : variant === "info"
        ? researchIcons.alertCircle
        : researchIcons.gift;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px] sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="promo-glass-modal relative w-full max-w-[420px] overflow-hidden rounded-3xl border border-white/25 bg-white/15 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.35)] backdrop-blur-xl sm:p-8 dark:border-white/15 dark:bg-black/35"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-primary/25 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-12 -left-8 h-32 w-32 rounded-full bg-white/20 blur-3xl dark:bg-white/10"
        />

        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-black/25 text-white transition-opacity hover:opacity-90"
          aria-label="Закрыть"
        >
          <Icon icon={researchIcons.close} size="sm" />
        </button>

        <VStack gap={5} align="center" width="100%" className="relative z-[1]">
          <div className="promo-glass-modal__icon flex h-16 w-16 items-center justify-center rounded-full border border-white/30 bg-white/20 text-white shadow-inner">
            <Icon icon={icon} size="lg" />
          </div>

          <VStack gap={2} align="center" width="100%">
            <Text
              weight="semibold"
              className="text-center text-lg text-white sm:text-xl"
            >
              {title}
            </Text>
            <Text
              weight="semibold"
              className="text-center text-base text-white/95"
            >
              {benefitLabel}
            </Text>
            <Text size="sm" className="text-center text-white/75">
              {description}
            </Text>
          </VStack>

          {error ? (
            <p className="w-full rounded-xl border border-red-300/40 bg-red-950/40 px-3 py-2 text-center text-sm text-red-100">
              {error}
            </p>
          ) : null}

          <Button
            type="button"
            label={busy ? "…" : ctaLabel}
            variant="primary"
            size="lg"
            isDisabled={busy}
            onClick={onPrimary}
          />
        </VStack>
      </div>
    </div>
  );
}
